import { type FC, type MouseEventHandler, type ReactNode, useState, useCallback } from 'react';
import { graphql, useFragment } from 'react-relay';
import { FormattedMessage } from 'dibs-react-intl';
import {
    trackEvent,
    eventNameConstants,
    interactionTypeConstants,
    stepInteractionConstants,
} from 'dibs-tracking';
import classnames from 'classnames';
import { Link } from 'dibs-elements/exports/Link';
import CheckShield from 'dibs-icons/exports/CheckShield';
import { useBuyerIncentivesMessages } from 'dibs-buyer-incentives/exports/useBuyerIncentivesMessages';
import { usePdpDibsPromiseVariant } from '../../hooks/usePdpDibsPromiseVariant';
import { PdpSharedDibsPromiseModalLazy } from '../../PdpSharedModals/PdpSharedDibsPromiseModal/PdpSharedDibsPromiseModalLazy';

import { type PdpSharedSidebarBuyerIncentives_user$key } from './__generated__/PdpSharedSidebarBuyerIncentives_user.graphql';

import dibsCss from 'dibs-css';

const Incentives: FC = () => {
    const incentives = useBuyerIncentivesMessages();

    return (
        <>
            {incentives.map((incentive, index) => (
                <div key={index} className={classnames(dibsCss.inline, dibsCss.whitespacePreWrap)}>
                    <FormattedMessage
                        id="PdpSharedSidebarBuyerIncentives.incentive"
                        defaultMessage="{isLast, select, true {{incentive}} other {{incentive}, }}"
                        values={{ incentive, isLast: index === incentives.length - 1 }}
                    />
                </div>
            ))}
        </>
    );
};

const userFragment = graphql`
    fragment PdpSharedSidebarBuyerIncentives_user on User {
        ...PdpSharedDibsPromiseModalLazy_user
    }
`;

const PdpSharedSidebarBuyerIncentives: FC<{
    onDetailsClick?: MouseEventHandler;
    user?: PdpSharedSidebarBuyerIncentives_user$key | null | undefined;
}> = ({ onDetailsClick, user: userRef }) => {
    const isDibsPromiseVariant = usePdpDibsPromiseVariant();
    const showLink = !!onDetailsClick || isDibsPromiseVariant;
    const [isModalOpen, setIsModalOpen] = useState(false);
    const user = useFragment(userFragment, userRef);

    const handleOpenModal = useCallback(() => {
        setIsModalOpen(true);
        trackEvent({
            eventName: eventNameConstants.EVENT_PRODUCT_INTERACTION,
            interaction_type: interactionTypeConstants.INTERACTION_TYPE_EBI_DETAILS,
            step_interaction_name:
                stepInteractionConstants.STEP_INTERACTION_EBI_SIDERAIL_DETAILS_CLICK,
        });
    }, []);

    const handleCloseModal = useCallback(() => {
        setIsModalOpen(false);
    }, []);

    const getLink = (chunk: ReactNode): ReactNode => {
        if (showLink) {
            return (
                <Link href="#" onClick={isDibsPromiseVariant ? handleOpenModal : onDetailsClick}>
                    {chunk}
                </Link>
            );
        } else {
            return null;
        }
    };

    return (
        <>
            {!isDibsPromiseVariant && (
                <span>
                    <FormattedMessage
                        id="PdpSharedSidebarBuyerIncentives.incentives.copy"
                        defaultMessage="<strong>The 1stDibs Promise:</strong> {incentives} <link>Details</link>"
                        values={{
                            strong: chunk => <strong>{chunk}</strong>,
                            incentives: <Incentives />,
                            link: chunk => getLink(chunk),
                        }}
                    />
                </span>
            )}

            {isDibsPromiseVariant && (
                <div
                    className={classnames(
                        dibsCss.pSmaller,
                        dibsCss.bgShagreen50,
                        dibsCss.roundedXxsmall
                    )}
                >
                    <div
                        className={classnames(
                            dibsCss.flex,
                            dibsCss.gapXsmall,
                            dibsCss.sassyFontBodySmallHeavy,
                            dibsCss.mbXxsmall
                        )}
                    >
                        <CheckShield variation="20" />
                        <FormattedMessage
                            id="PdpSharedSidebarBuyerIncentives.incentives.title"
                            defaultMessage="Shop With Confidence"
                        />
                    </div>
                    <span className={classnames(dibsCss.sassyFontBodySmall)}>
                        <FormattedMessage
                            id="PdpSharedSidebarBuyerIncentives.incentives.message"
                            defaultMessage="{incentives} <link>Details</link>"
                            values={{
                                incentives: <Incentives />,
                                link: chunk => getLink(chunk),
                            }}
                        />
                    </span>
                    <PdpSharedDibsPromiseModalLazy
                        user={user}
                        isOpen={isModalOpen}
                        onClose={handleCloseModal}
                    />
                </div>
            )}
        </>
    );
};

export default PdpSharedSidebarBuyerIncentives;
